import { FormField, MessageBox, Select } from "@rothoblaas/design-system";
import type { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useI18n from "../../../hooks/useTranslations";
import type { ScrewForSettings } from "../../../http/deckDesigner/types/screws";
import type { Settings } from "../../../http/deckDesigner/types/settings";
import type { SupportFamilyForSettings } from "../../../http/deckDesigner/types/supportFamilies";
import type { SupportForSettings } from "../../../http/deckDesigner/types/supports";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import styles from "./ConnectorsCard.module.css";
import FamilyOptionsGrid from "./FamilyOptionsGrid";
import { SUPPORT_FAMILY_IMAGE_BY_CODE } from "./familyImages";
import { getItemOptionLabel, getOfferedOption } from "./itemOption";
import type { SupportOptions } from "./useSupportOptions";

type SupportFieldsProps = {
  defaultValues: DeckDesignerConfigurationFormValues;
  settings: Settings | undefined;
  hasError: boolean | undefined;
  supportOptions: SupportOptions;
  // Called after the field's own change, for the card's auto-select bookkeeping.
  onSupportFamilyChange: (supportFamily: SupportFamilyForSettings) => void;
  onSupportChange: (support: SupportForSettings | null) => void;
  onShowCompatibleValues: () => void;
};

// The support part of ConnectorsCard: support family, support and its screw.
const SupportFields: FC<SupportFieldsProps> = ({
  defaultValues,
  settings,
  hasError,
  supportOptions,
  onSupportFamilyChange,
  onSupportChange,
  onShowCompatibleValues,
}) => {
  const i18n = useI18n();
  const isReadOnly = useReadOnly();
  const { control } = useFormContext<DeckDesignerConfigurationFormValues>();
  const calculationTranslation = i18n.translation.deckDesignerModule.configurations.calculation;
  const errorsTranslation = i18n.translation.deckDesignerModule.configurations.errors;
  const supports = settings?.supports;
  const screws = settings?.screws;
  const {
    supportsAvailableForMaterialClass,
    filteredSupportFamilies,
    hasNoSupportFamilyMapping,
    hasNoSupportFamilyFittingHeight,
    hasNoSupportFamilyFittingSpacing,
    filteredSupports,
    filteredSupportById,
    shouldShowSupportScrew,
    filteredSupportScrews,
    filteredSupportScrewById,
    requiredSupportHeightRange,
    supportSlopeDrop,
  } = supportOptions;

  const slopeDropTranslation = errorsTranslation.noSupportForSlopeDrop;
  const supportFamilyEmptyReason = hasNoSupportFamilyMapping ? (
    errorsTranslation.noCompatibleSupportFamilyMapping
  ) : hasNoSupportFamilyFittingSpacing ? (
    errorsTranslation.noSupportForSpacing
  ) : !hasNoSupportFamilyFittingHeight ? undefined : !requiredSupportHeightRange ? (
    errorsTranslation.invalidTotalHeight
  ) : supportSlopeDrop ? (
    <span className={styles.messageBlock}>
      <strong>{slopeDropTranslation.title}</strong>
      <ul className={styles.messageList}>
        <li>{slopeDropTranslation.requiredRange(requiredSupportHeightRange.min, requiredSupportHeightRange.max)}</li>
        <li>{slopeDropTranslation.widestSupport(supportSlopeDrop.itemCode, supportSlopeDrop.widestRangeMm)}</li>
        <li>{slopeDropTranslation.fix(supportSlopeDrop.maxSlopeDegrees)}</li>
      </ul>
    </span>
  ) : (
    errorsTranslation.noSupportForRequiredHeightRange(requiredSupportHeightRange.min, requiredSupportHeightRange.max)
  );

  return (
    <>
      {supportFamilyEmptyReason && (
        <MessageBox
          type="warning"
          content={
            supportsAvailableForMaterialClass?.length ? (
              <>
                {supportFamilyEmptyReason}{" "}
                <button type="button" className={styles.linkButton} onClick={onShowCompatibleValues}>
                  {calculationTranslation.showCompatibleValues}
                </button>
              </>
            ) : (
              supportFamilyEmptyReason
            )
          }
        />
      )}
      <Controller
        name="supportFamily"
        control={control}
        defaultValue={defaultValues.supportFamily}
        render={({ field }) => (
          <FormField
            label={i18n.translation.deckDesignerModule.supportFamilies.singular}
            warning={hasError ? i18n.translation.common.errorLoadingData : undefined}
            info={supportFamilyEmptyReason ? errorsTranslation.noSupportFamilyOptions : undefined}
          >
            {!!filteredSupportFamilies?.length && (
              <FamilyOptionsGrid
                options={filteredSupportFamilies}
                value={field.value}
                imageByCode={SUPPORT_FAMILY_IMAGE_BY_CODE}
                isLoading={!hasError && !supports}
                hasError={!!hasError}
                onSelect={(option) => {
                  field.onChange(option);
                  onSupportFamilyChange(option);
                }}
              />
            )}
          </FormField>
        )}
      />

      <Controller
        name="support"
        control={control}
        defaultValue={defaultValues.support}
        render={({ field, fieldState }) => (
          <FormField
            label={i18n.translation.deckDesignerModule.supports.singular}
            error={fieldState.error}
            warning={hasError ? i18n.translation.common.errorLoadingData : undefined}
            info={
              filteredSupports?.length === 1 && !!field.value
                ? i18n.translation.common.singleOptionAvailable
                : undefined
            }
          >
            {({ labelId, isInvalid }) => (
              <Select<SupportForSettings, false>
                {...field}
                inputId={labelId}
                value={getOfferedOption(filteredSupportById, field.value)}
                options={filteredSupports}
                getOptionValue={(option) => option.item.id}
                getOptionLabel={getItemOptionLabel}
                placeholder={`${i18n.translation.common.select}...`}
                isLoading={!supports && !hasError}
                isDisabled={
                  isReadOnly ||
                  !supports ||
                  !!hasError ||
                  !filteredSupports?.length ||
                  (filteredSupports.length === 1 && !!field.value)
                }
                isClearable
                isInvalid={isInvalid}
                onChange={(selectedOption) => {
                  const nextSupport = selectedOption ?? null;

                  field.onChange(nextSupport);
                  onSupportChange(nextSupport);
                }}
              />
            )}
          </FormField>
        )}
      />

      {shouldShowSupportScrew && (
        <Controller
          name="supportScrew"
          control={control}
          defaultValue={defaultValues.supportScrew}
          render={({ field, fieldState }) => (
            <FormField
              label={calculationTranslation.supportScrew}
              error={fieldState.error}
              warning={hasError ? i18n.translation.common.errorLoadingData : undefined}
            >
              {({ labelId, isInvalid }) => (
                <Select<ScrewForSettings, false>
                  {...field}
                  inputId={labelId}
                  value={getOfferedOption(filteredSupportScrewById, field.value)}
                  options={filteredSupportScrews}
                  getOptionValue={(option) => option.item.id}
                  getOptionLabel={getItemOptionLabel}
                  placeholder={`${i18n.translation.common.select}...`}
                  isLoading={!screws && !hasError}
                  isDisabled
                  isInvalid={isInvalid}
                />
              )}
            </FormField>
          )}
        />
      )}
    </>
  );
};

export default SupportFields;
